import { useMemo } from 'react';
import { OrgUser } from '@/models/User/OrgUser';
import { AuthIdContactRow } from './EmergencyRespondersPane';

interface Group {
  group_value: OrgUser[];
}

interface UseEmergencyContactRowsParams {
  groups: Group[] | null;
  contactAuthIds: { [key: string]: string } | undefined;
}

/**
 * Custom hook to transform emergency contact auth_ids into rows with user information.
 * Searches across all users in all groups to ensure contacts are properly paired with user data,
 * even if they're not in the current context (e.g., a specific group).
 */
export const useEmergencyContactRows = ({
  groups,
  contactAuthIds,
}: UseEmergencyContactRowsParams) => {
  // Get all users from all groups (deduplicated by auth_id)
  const allOrgUsers = useMemo(() => {
    if (!groups) return [];

    const userMap = new Map<string, OrgUser>();
    groups.forEach((group) => {
      group.group_value.forEach((user) => {
        if (!userMap.has(user.auth_id)) {
          userMap.set(user.auth_id, user);
        }
      });
    });

    return Array.from(userMap.values());
  }, [groups]);

  // Transform auth_id contacts to DataGrid rows
  const authIdContactRows: AuthIdContactRow[] = useMemo(() => {
    if (!contactAuthIds) return [];

    return Object.entries(contactAuthIds).map(
      ([key, authId]: [string, string]) => {
        const user = allOrgUsers.find((u) => u.auth_id === authId);

        return {
          id: key,
          auth_id: authId,
          first_name: user?.first_name ?? 'Unknown',
          last_name: user?.last_name ?? '',
          phone_number: user?.phone_number ?? '',
          role: user?.role ?? '',
        };
      },
    );
  }, [contactAuthIds, allOrgUsers]);

  return { authIdContactRows, allOrgUsers };
};
